<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  label: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  message: { type: String, default: '' },
  messageType: { type: String, default: 'info' },
  rows: { type: Number, default: 4 },
  availableVariables: {
    type: Array,
    default: () => [],
  },
  availableVariableDetails: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();

const textareaRef = ref(null);
const showVariableShortcuts = ref(false);

const helperButtons = [
  { label: 'B', wrapper: '*', title: 'Negrito' },
  { label: 'I', wrapper: '_', title: 'Italico' },
  { label: 'S', wrapper: '~', title: 'Tachado' },
  { label: '{ }', wrapper: '```', title: 'Monospace' },
];
const variableShortcuts = computed(() => {
  if (props.availableVariableDetails.length) {
    return props.availableVariableDetails
      .map(item => ({
        key: String(item?.key || '').trim(),
        description: String(item?.description || '').trim(),
      }))
      .filter(item => item.key);
  }

  return Array.from(
    new Set(
      (Array.isArray(props.availableVariables) ? props.availableVariables : [])
        .map(variable => String(variable || '').trim())
        .filter(Boolean)
    )
  ).map(variable => ({
    key: variable,
    description: '',
  }));
});

const messageClass = computed(() => {
  if (props.messageType === 'error') return 'text-n-ruby-9 dark:text-n-ruby-9';
  if (props.messageType === 'success')
    return 'text-n-teal-10 dark:text-n-teal-10';
  return 'text-n-slate-11 dark:text-n-slate-11';
});

const insertTextAtSelection = text => {
  const textarea = textareaRef.value;
  if (!textarea) return;

  const value = props.modelValue || '';
  const start = textarea.selectionStart ?? value.length;
  const end = textarea.selectionEnd ?? value.length;
  const nextValue = `${value.slice(0, start)}${text}${value.slice(end)}`;

  emit('update:modelValue', nextValue);

  requestAnimationFrame(() => {
    textarea.focus();
    const cursorPosition = start + text.length;
    textarea.setSelectionRange(cursorPosition, cursorPosition);
  });
};

const wrapSelection = wrapper => {
  const textarea = textareaRef.value;
  if (!textarea) return;

  const value = props.modelValue || '';
  const start = textarea.selectionStart ?? value.length;
  const end = textarea.selectionEnd ?? value.length;
  const selectedText = value.slice(start, end) || 'texto';
  const wrappedText = `${wrapper}${selectedText}${wrapper}`;
  const nextValue = `${value.slice(0, start)}${wrappedText}${value.slice(end)}`;

  emit('update:modelValue', nextValue);

  requestAnimationFrame(() => {
    textarea.focus();
    const cursorStart = start + wrapper.length;
    const cursorEnd = cursorStart + selectedText.length;
    textarea.setSelectionRange(cursorStart, cursorEnd);
  });
};

const handleInput = event => {
  emit('update:modelValue', event.target.value);
};

const formatVariableToken = variable => `{{${variable}}}`;

const insertVariable = variable => {
  insertTextAtSelection(formatVariableToken(variable));
  showVariableShortcuts.value = false;
};
</script>

<template>
  <div class="relative flex flex-col gap-1">
    <label v-if="label" class="mb-0.5 text-sm font-medium text-n-slate-12">
      {{ label }}
    </label>

    <div class="border rounded-lg border-n-weak bg-n-alpha-black2">
      <div class="flex items-center gap-2 px-3 py-2 border-b border-n-weak">
        <Button
          v-for="button in helperButtons"
          :key="button.label"
          variant="ghost"
          size="xs"
          type="button"
          :label="button.label"
          :title="button.title"
          @click="wrapSelection(button.wrapper)"
        />
        <div v-if="variableShortcuts.length" class="relative">
          <Button
            data-test="variable-shortcuts-trigger"
            variant="ghost"
            size="xs"
            type="button"
            :label="t('CAMPAIGN.ENHANCED.FIELDS.VARIABLE_SHORTCUTS.BUTTON')"
            :title="t('CAMPAIGN.ENHANCED.FIELDS.VARIABLE_SHORTCUTS.TITLE')"
            @click="showVariableShortcuts = !showVariableShortcuts"
          />
          <div
            v-if="showVariableShortcuts"
            class="absolute left-0 z-10 mt-2 w-72 rounded-lg border border-n-weak bg-n-solid-1 p-3 shadow-lg"
          >
            <p class="mb-2 text-xs font-medium text-n-slate-12">
              {{ t('CAMPAIGN.ENHANCED.FIELDS.VARIABLE_SHORTCUTS.TITLE') }}
            </p>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="variable in variableShortcuts"
                :key="variable.key"
                v-tooltip.top="variable.description"
                :title="variable.description"
                class="inline-flex"
              >
                <Button
                  :label="formatVariableToken(variable.key)"
                  data-test="variable-shortcut-option"
                  variant="ghost"
                  size="xs"
                  type="button"
                  @click="insertVariable(variable.key)"
                />
              </span>
            </div>
          </div>
        </div>
      </div>

      <textarea
        ref="textareaRef"
        :value="modelValue"
        :placeholder="placeholder"
        :rows="rows"
        class="w-full p-3 text-sm border-0 resize-y bg-transparent text-n-slate-12 placeholder:text-n-slate-10 outline-none min-h-[10rem]"
        @input="handleInput"
      />
    </div>

    <p
      v-if="message"
      class="min-w-0 mt-1 mb-0 text-xs truncate transition-all duration-500 ease-in-out"
      :class="messageClass"
    >
      {{ message }}
    </p>
  </div>
</template>
